import { useCartDrawer } from '@iheartjane/cart';
import { clearCheckoutError, useCheckoutStatus } from '@iheartjane/cart/utils';
import { Box, Button, Flex, Modal as ReeferModal } from '@iheartjane/reefer';
import isEmpty from 'lodash-es/isEmpty';
import noop from 'lodash-es/noop';
import { useLocation, useNavigate } from 'react-router';

import { useCartActions } from '@jane/monolith/components/cart/cartFacade/useCartActions';
import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import {
  clearCart,
  clearRemovedCrmRedemptions,
  getRemoteCart,
  removeBrandSpecialHasChangedFlag,
  removeSpecialHasChangedFlag,
  selectCart,
  unavailableCheckoutFixed,
} from '@jane/monolith/customer/redux/cart';
import { selectCustomer } from '@jane/monolith/customer/redux/customer';
import { closeModal } from '@jane/monolith/customer/redux/modal';
import { selectStore } from '@jane/monolith/customer/redux/store';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { useGuestCheckoutEnabled } from '@jane/monolith/hooks/useGuestCheckoutEnabled';
import { useIframeScroll, useIsInIframe } from '@jane/shared-ecomm/hooks';
import { useEcommApp } from '@jane/shared/providers';
import {
  monolithPaths as paths,
  postMessageToIframeParent,
} from '@jane/shared/util';

import { CartAlertsForm } from './form';

export const CartAlerts = ({
  goBack,
  isCheckoutAlert,
  onClose,
}: {
  goBack?: () => void;
  isCheckoutAlert?: boolean;
  onClose?: () => void;
}) => {
  const navigate = useNavigate();
  const { cart, isFixingUnavailableCheckout } = useCustomerSelector(selectCart);
  const { checkoutErrorDetails } = useCheckoutStatus();
  const { store } = useCustomerSelector(selectStore);
  const { authenticated } = useCustomerSelector(selectCustomer);
  const isGuestCheckoutEnabled = useGuestCheckoutEnabled();

  const { isEmbed } = useEcommApp();
  const isInIframe = useIsInIframe();
  const dispatch = useCustomerDispatch();
  const { deleteUnavailableProducts } = useCartActions();
  const { close: closeCartDrawer } = useCartDrawer();

  const {
    brand_special_has_changed,
    brand_special_unavailable_reason,
    special_has_changed,
    unavailable_products,
    products,
    removed_crm_redemptions,
    quantity_adjusted_products,
  } = cart;

  const buttonLabel = checkoutErrorDetails
    ? 'Back to checkout'
    : brand_special_has_changed
      ? 'OK'
      : 'Got it!';

  const { pathname } = useLocation();
  const onCheckoutPage = /\/cart\/(guest_)?checkout/.test(pathname);

  const onClick = (doClose: boolean) => {
    if (unavailable_products.length && !products.length) {
      closeCartDrawer();
      dispatch(clearCart());
    }
    if (unavailable_products.length) {
      deleteUnavailableProducts(unavailable_products);
    }

    if (brand_special_has_changed || brand_special_unavailable_reason) {
      dispatch(removeBrandSpecialHasChangedFlag());
    }

    if (special_has_changed) {
      dispatch(removeSpecialHasChangedFlag());
    }

    if (removed_crm_redemptions) {
      dispatch(clearRemovedCrmRedemptions());
    }

    if (isCheckoutAlert || checkoutErrorDetails) {
      clearCheckoutError();
    }

    if (checkoutErrorDetails && !onCheckoutPage) {
      closeCartDrawer();
      navigate(
        !authenticated && isGuestCheckoutEnabled
          ? paths.guestCheckout()
          : paths.checkout()
      );
    }

    if (isFixingUnavailableCheckout) {
      dispatch(unavailableCheckoutFixed());

      // cart has been reseted by checkout
      if (cart.uuid === '' && unavailable_products.length === 0) {
        if (goBack) {
          goBack();
        }
        // cart was emptied by the cartdrawer
      } else if (cart.uuid && isEmpty(cart.products)) {
        closeCartDrawer();
        dispatch(clearCart());
      }
    }

    if (quantity_adjusted_products && quantity_adjusted_products.length > 0) {
      dispatch(getRemoteCart(cart.uuid));
    }

    // if we have an onClose behavior ignore the redux dispatch
    if (doClose && !onClose) {
      dispatch(closeModal());
    } else if (onClose) {
      onClose();
    }
  };

  useIframeScroll(isInIframe);

  if (isEmbed && !isCheckoutAlert) {
    return (
      <Box mx="auto" maxWidth={360} pt={24}>
        <CartAlertsForm
          checkoutErrorDetails={checkoutErrorDetails}
          isFixingUnavailableCheckout={isFixingUnavailableCheckout}
          cart={cart}
          store={store}
        />
        <Button
          mt={24}
          full
          variant="primary"
          label={buttonLabel}
          onClick={() => {
            if (unavailable_products) {
              postMessageToIframeParent({
                messageType: 'unavailableProductsEvent',
                payload: {
                  unavailable_products,
                  products,
                },
              });
            }
            onClick(false);
          }}
        />
      </Box>
    );
  }

  if (isCheckoutAlert && isEmbed) {
    return (
      <ReeferModal
        appId="root"
        open
        onRequestClose={onClose ?? noop}
        variant="dialogue"
        overflow="hidden"
        topOverride={isInIframe ? '160px' : undefined}
      >
        <Flex maxWidth={325} flexDirection="column" p={24}>
          <CartAlertsForm
            checkoutErrorDetails={checkoutErrorDetails}
            isFixingUnavailableCheckout={isFixingUnavailableCheckout}
            cart={cart}
            store={store}
          />

          <Button
            mt={24}
            full
            variant="primary"
            label={buttonLabel}
            onClick={() => onClick(false)}
          />
        </Flex>
      </ReeferModal>
    );
  }

  return (
    <ReeferModal open variant="dialogue" onRequestClose={() => onClick(true)}>
      <ReeferModal.Content>
        <Flex width="279px" flexDirection="column" alignItems="center">
          <CartAlertsForm
            checkoutErrorDetails={checkoutErrorDetails}
            cart={cart}
            isFixingUnavailableCheckout={isFixingUnavailableCheckout}
            store={store}
          />

          <Button
            mt={24}
            full
            variant="primary"
            label={buttonLabel}
            onClick={() => onClick(true)}
          />
        </Flex>
      </ReeferModal.Content>
    </ReeferModal>
  );
};
